import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  requestNotificationPermission, 
  isNotificationEnabled, 
  getNotificationPermission 
} from '../utils/notificationUtils';

const NotificationSettings = ({ isOpen, onClose }) => {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported('Notification' in window);
    setPermission(getNotificationPermission());
  }, []);

  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    
    if (granted) {
      setPermission('granted');
      toast.success('🔔 Notifications enabled! You\'ll now receive chat notifications.');
    } else {
      toast.error('❌ Notification permission denied. You can enable it in your browser settings.');
    }
  };

  const handleTestNotification = () => {
    if (isNotificationEnabled()) {
      new Notification('🎉 VestrioZ Test Notification', {
        body: 'Great! Your notifications are working perfectly.',
        icon: '/images/VESTRIOZ.png',
        tag: 'test-notification'
      });
      toast.success('Test notification sent!');
    } else {
      toast.error('Please enable notifications first.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-dark-charcoal flex items-center">
              <i className="fas fa-bell mr-3 text-sage-green"></i>
              Notification Settings
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!isSupported ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-dark-charcoal mb-2">
                Notifications Not Supported
              </h3>
              <p className="text-medium-gray">
                Your browser doesn't support notifications. Please use a modern browser like Chrome, Firefox, or Safari.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Current Status */}
              <div className="bg-light-beige/30 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-dark-charcoal">Current Status</h3>
                    <p className="text-sm text-medium-gray mt-1">
                      {permission === 'granted' ? 'Notifications are enabled' :
                       permission === 'denied' ? 'Notifications are blocked' :
                       'Notifications not configured'}
                    </p>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${
                    permission === 'granted' ? 'bg-green-500' :
                    permission === 'denied' ? 'bg-red-500' :
                    'bg-yellow-500'
                  }`}></div>
                </div>
              </div>

              {/* Benefits */}
              <div>
                <h3 className="font-semibold text-dark-charcoal mb-3">
                  Why enable notifications?
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-comments text-sage-green mt-1"></i>
                    <div>
                      <p className="font-medium text-dark-charcoal">Instant Chat Messages</p>
                      <p className="text-sm text-medium-gray">Get notified when someone sends you a message</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-question-circle text-sage-green mt-1"></i>
                    <div>
                      <p className="font-medium text-dark-charcoal">New Inquiries</p>
                      <p className="text-sm text-medium-gray">Know when someone is interested in your listings</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-image text-sage-green mt-1"></i>
                    <div>
                      <p className="font-medium text-dark-charcoal">Image Messages</p>
                      <p className="text-sm text-medium-gray">See when someone shares photos with you</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {permission !== 'granted' && (
                  <button
                    onClick={handleEnableNotifications}
                    className="w-full bg-sage-green text-white py-3 px-4 rounded-xl font-semibold hover:bg-sage-green/90 transition-colors duration-200 flex items-center justify-center"
                  >
                    <i className="fas fa-bell mr-2"></i>
                    Enable Notifications
                  </button>
                )}

                {permission === 'granted' && (
                  <button
                    onClick={handleTestNotification}
                    className="w-full bg-dark-charcoal text-white py-3 px-4 rounded-xl font-semibold hover:bg-dark-charcoal/90 transition-colors duration-200 flex items-center justify-center"
                  >
                    <i className="fas fa-test-tube mr-2"></i>
                    Test Notification
                  </button>
                )}

                {permission === 'denied' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <i className="fas fa-info-circle text-red-600 mt-1"></i>
                      <div>
                        <p className="font-medium text-red-800">Notifications Blocked</p>
                        <p className="text-sm text-red-600 mt-1">
                          To enable notifications, click the 🔒 icon in your browser's address bar and allow notifications for VestrioZ.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Privacy Note */}
              <div className="bg-sage-green/10 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <i className="fas fa-shield-alt text-sage-green mt-1"></i>
                  <div>
                    <p className="font-medium text-dark-charcoal">Privacy & Control</p>
                    <p className="text-sm text-medium-gray mt-1">
                      You can disable notifications anytime in your browser settings. We only send notifications for important chat messages and inquiries.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;